import { isBlank } from './BooleanUtility';

export const processBatches = async (items, processFn, options = {}) => {
  if (isBlank(items)) return [];

  const {
    batchSize = 20,
    concurrent = false,
    onBatchComplete,
    onError,
  } = options;

  const results = [];
  const totalBatches = Math.ceil(items.length / batchSize);

  try {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      let batchResults;

      try {
        if (concurrent) {
          // Process items within the batch concurrently
          const promises = batch.map(async (item) => {
            try {
              return await processFn(item);
            } catch (error) {
              if (onError) onError(error, item);
              return null; // Return null for failed items
            }
          });
          batchResults = (await Promise.all(promises)).filter(result => result !== null);
        } else {
          // Process the entire batch sequentially
          batchResults = await processFn(batch);
        }

        // Store results (flatten if concurrent, push directly if sequential)
        if (Array.isArray(batchResults)) {
          results.push(...batchResults);
        } else if (batchResults !== undefined) {
          results.push(batchResults);
        }

        // Trigger simplified batch completion callback
        if (onBatchComplete) {
          onBatchComplete(batchNumber, totalBatches);
        }
      } catch (error) {
        if (onError) {
          onError(error, batch);
        } else {
          console.error(`Error processing batch ${batchNumber}:`, error);
        }
      }
    }

    return results;
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      console.error('Error in batch processing:', error);
    }
    throw error;
  }
};

// For backward compatibility with createThrottledBatchProcessor
export const createThrottledBatchProcessor = (options = {}) => {
  return (items, processFn, callbacks = {}) => {
    return processBatches(items, processFn, { 
      ...options, 
      concurrent: true, // Default to concurrent for backward compatibility
      ...callbacks 
    });
  };
};